import { DealRowActionType } from '@openx/components-deal/constants.ts';
import { DealsBulkOperations } from '@openx/components-deal/lib/DealsList/DealBulkOperations.tsx';
import type { DealActionResult } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton.tsx';
import { HeaderActions } from '@openx/components-header/HeaderActions/HeaderActions.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import type { OAListDeal } from '@openx/types/deal.ts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealLandingExport } from '../../configs/deals/utils/useDealLandingExport.ts';
import { useListCriteria } from '../../utils/useListCriteria.ts';

import { DealsList } from './DealsList/DealsList.tsx';
import { dealsDefaultCriteria } from './DealsList/dealsCriteria.ts';

export function DealsListPage() {
  const { t } = useTranslation();
  const [selectedDeals, setSelectedDeals] = useState<OAListDeal[]>([]);
  const { criteria, setCriteria } = useListCriteria('deals', dealsDefaultCriteria);
  const { exportDeals, isExporting } = useDealLandingExport(criteria);

  const actions: Array<ActionButtonProps> = [
    {
      color: 'primary',
      'data-test': 'create-deal-button',
      href: '/deals/create',
      isAllowed: true,
      label: t('Create New Deal'),
      showAsActionButton: true,
    },
    {
      color: 'secondary',
      'data-test': 'export-deal-button',
      isAllowed: true,
      label: t('Export CSV'),
      loading: isExporting,
      onClick: exportDeals,
      showAsActionButton: true,
    },
  ];

  const onCallDealActionFinish = useCallback((result?: DealActionResult) => {
    if (!result) {
      return;
    }

    const { fulfilledIds } = result;

    if (fulfilledIds.length) {
      setSelectedDeals(selectedDeals =>
        selectedDeals.filter(item => {
          const matchesById = fulfilledIds.includes(item.id);
          const matchesByUid = item.uid ? fulfilledIds.includes(item.uid) : false;

          return !matchesById && !matchesByUid;
        })
      );
    }
  }, []);

  const bulkOperationRender = useCallback(
    () => (
      <DealsBulkOperations
        selectedDeals={selectedDeals}
        onClose={() => {
          setSelectedDeals([]);
        }}
        availableActions={[
          DealRowActionType.PAUSE,
          DealRowActionType.RESUME,
          DealRowActionType.DELETE,
          DealRowActionType.DOWNLOAD,
        ]}
        onActionFinish={onCallDealActionFinish}
      />
    ),
    [selectedDeals, onCallDealActionFinish]
  );

  return (
    <PageHeader
      titlePrefix="OpenXSelect"
      title={t('Deals List')}
      render={() => <HeaderActions actions={actions} />}
      emptyMode={!!selectedDeals.length}
      emptyModeRender={bulkOperationRender}
      sticky
    >
      <DealsList
        criteria={criteria}
        setCriteria={setCriteria}
        selectedDeals={selectedDeals}
        setSelectedDeals={setSelectedDeals}
      />
    </PageHeader>
  );
}
